import ReviewCard, { ReviewContent, ReviewSection } from "@/components/workflow/ReviewCard";
import { PASSPORT_ID_TYPES } from "@/pages/workflow/address";
import { Badge, HStack, Text } from "@chakra-ui/react";
import { ENTITY, JOINT_SUB_TYPE, TA_ACCOUNT_TYPE, US_CITIZEN } from "@equidefi/constants/profile";
import { CurrencyFormatter } from "@equidefi/i18n";
import { HighlightCard } from "@equidefi/portals/components/investments/HighlightCard";
import { useGetInvestmentVaults } from "@equidefi/portals/hooks/useVault";
import {
  INDIVIDUAL,
  blankIfNA,
  canEditPaymentMethod,
  governmentIdLabel,
  offeringHidesSecurityLabel,
  userFullName,
} from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types";
import { DisplayTaxID, Heading, TooltipTime } from "@equidefi/ui";
import { useMemo } from "react";
import { useWorkflowContext } from "../context/WorkflowContext";

const TA_SUB_TYPE_LABEL: Record<string, string> = {
  INDIVIDUAL: "Account Type",
  JOINT: "Joint Type",
  ENTITY: "Entity Type",
  TRUST: "Trust Type",
};

export const ReviewInvestment = () => {
  const { offering, investment } = useWorkflowContext() as {
    offering: DTO.Offering;
    investment: Required<DTO.Investment>;
  };

  const { data: vaultRecords } = useGetInvestmentVaults(investment?.id);
  const hideSecurityLabel = offeringHidesSecurityLabel(offering);
  const isEntity = investment.vault.entity_type === ENTITY;

  const nonPrimaryVaultRecords: any = useMemo(() => vaultRecords?.filter((v) => !v.is_primary) ?? [], [vaultRecords]);
  const accountTypeLabel = useMemo(() => {
    const { ta_account_type, ta_sub_type } = investment.vault;
    return ta_account_type ? TA_ACCOUNT_TYPE[ta_account_type] : ta_sub_type;
  }, [investment.vault]);

  const subTypeLabel = investment.vault.ta_account_type ? TA_SUB_TYPE_LABEL[investment.vault.ta_account_type] : "Type";

  const subType = useMemo(() => {
    const { ta_account_type, ta_sub_type } = investment.vault;
    if (ta_account_type === "JOINT" && ta_sub_type) {
      return `${JOINT_SUB_TYPE[ta_sub_type]} (${ta_sub_type})`;
    }
    return ta_sub_type;
  }, [investment.vault]);

  const sharesIssuedTo = useMemo(() => {
    switch (investment.vault.ta_account_type) {
      case "INDIVIDUAL":
        return userFullName(investment.vault);
      case "JOINT":
        return `${userFullName(investment.vault)} and ${userFullName(investment.signing_parties[2])}`;
      case "TRUST":
      case "ENTITY":
        return investment.vault.company;
    }
    return null;
  }, [investment.vault, investment.signing_parties]);

  return (
    <>
      <HighlightCard title="Issue Securities To" text={sharesIssuedTo} />
      <ReviewCard title="Investment Amount">
        <ReviewSection page="subscription" isEditable={investment?.payment_editable}>
          <ReviewContent label="Amount">{CurrencyFormatter.format(investment.amount)}</ReviewContent>
          {!hideSecurityLabel && (
            <ReviewContent label={offering?.security_type_plural}>{investment?.quantity}</ReviewContent>
          )}
        </ReviewSection>
        <ReviewSection page="payment" isEditable={canEditPaymentMethod(investment)}>
          <ReviewContent label="Payment Method">{investment.payment_type}</ReviewContent>
        </ReviewSection>
      </ReviewCard>

      <ReviewCard title="Investor Details">
        <ReviewSection page="profile">
          <ReviewContent label="Investor Type">{accountTypeLabel}</ReviewContent>
        </ReviewSection>

        {investment.vault.ta_account_type !== INDIVIDUAL && (
          <ReviewSection page="signers">
            <ReviewContent label={subTypeLabel}>{subType}</ReviewContent>
          </ReviewSection>
        )}

        <ReviewSection page="subscription" isEditable={investment?.payment_editable}>
          <ReviewContent label="Accredited Investor">{investment.vault.accredited ? "Yes" : "No"}</ReviewContent>
          <ReviewContent label="Broker-Dealer Affiliate">{investment.vault.broker_dealer ? "Yes" : "No"}</ReviewContent>
        </ReviewSection>
        {isEntity && (
          <ReviewSection page="signers">
            <Heading fontWeight="bold">Entity Details</Heading>
            <ReviewContent label="Name">{investment.vault.company}</ReviewContent>
            {investment.vault.entity_address_street_1 && (
              <>
                <Heading fontWeight="bold">Entity Address</Heading>
                <Text>{`${investment.vault.entity_address_street_1}
                    ${investment.vault.entity_address_street_2 ?? ""}`}</Text>

                <Text>{`${investment.vault.entity_address_city}
                      ${investment.vault.entity_address_state}, ${blankIfNA(investment.vault.entity_address_postal_code)}`}</Text>
                <Text>{investment.vault.entity_address_country_code}</Text>
              </>
            )}
            <DisplayTaxID value={investment.vault?.ein} label="TIN" labelProps={{ fontWeight: "bold" }} />
          </ReviewSection>
        )}
      </ReviewCard>

      <ReviewCard title="Primary Signer">
        <ReviewSection page="profile">
          <ReviewContent label="Name">{userFullName(investment.vault)}</ReviewContent>
          <ReviewContent label="Phone">{investment.vault.phone_number}</ReviewContent>
        </ReviewSection>
        <ReviewSection page="address" returnTo="signers">
          <Heading fontWeight="bold">Legal Address</Heading>
          <Text>{`${investment.vault.address_street_1}
                    ${investment.vault.address_street_2 ?? ""}`}</Text>

          <Text>{`${investment.vault.address_city}
                      ${investment.vault.address_state}, ${blankIfNA(investment.vault.address_postal_code)}`}</Text>
          <Text>{investment.vault.address_country_code}</Text>

          <ReviewContent label="Citizenship">
            {investment.vault.citizenship === US_CITIZEN ? "US Citizen" : "International"}
          </ReviewContent>
          <ReviewContent label="DOB">
            <TooltipTime format={"MM/DD/YYYY"} datetime={investment.vault.birthdate}>
              {investment.vault.birthdate}
            </TooltipTime>
          </ReviewContent>
        </ReviewSection>
        {!isEntity && investment?.vault?.identification_type && (
          <ReviewSection className="fvMask" page="address">
            <Heading fontWeight="bold">Government ID</Heading>
            <ReviewContent label="Type">{governmentIdLabel(investment.vault.identification_type)}</ReviewContent>
            <ReviewContent label="ID Number">{investment.vault.identification_number}</ReviewContent>
            <ReviewContent
              label={
                !PASSPORT_ID_TYPES.includes(investment?.vault?.identification_type) ? "Issuing State" : "State of Birth"
              }>
              {investment.vault.issuing_authority}
            </ReviewContent>
            <ReviewContent label="Issuing Date">
              <TooltipTime format={"MM/DD/YYYY"} datetime={investment.vault.id_issue_date}>
                {investment.vault.id_issue_date}
              </TooltipTime>
            </ReviewContent>
            <ReviewContent label="Expiry Date">
              <TooltipTime format={"MM/DD/YYYY"} datetime={investment.vault.id_expire_date}>
                {investment.vault.id_expire_date}
              </TooltipTime>
            </ReviewContent>
          </ReviewSection>
        )}
        <ReviewSection page="signers">
          <DisplayTaxID
            value={investment.vault?.tax_id}
            label={investment.vault.citizenship === US_CITIZEN ? "SSN/TIN" : "TIN"}
            labelProps={{ fontWeight: "bold" }}
          />
        </ReviewSection>
      </ReviewCard>

      {investment.vault.ta_account_type !== "INDIVIDUAL" && (
        <ReviewCard title="Additional Individuals">
          {nonPrimaryVaultRecords.length > 0 ? (
            nonPrimaryVaultRecords.map((record: any) => (
              <ReviewSection key={record.id} page="signers">
                <ReviewContent label="Name">{userFullName(record.vault)}</ReviewContent>
                <ReviewContent label="Email">{record.email}</ReviewContent>
                <ReviewContent label="Phone">{record.vault.phone_number}</ReviewContent>
                <HStack spacing="4" mt={3} mb={1}>
                  {record.vault?.is_signer && <Badge>SIGNER</Badge>}
                  {record.vault?.is_kyc && <Badge>KYC</Badge>}
                </HStack>
              </ReviewSection>
            ))
          ) : (
            <ReviewSection page="signers">
              <Text>No additional individuals added</Text>
            </ReviewSection>
          )}
        </ReviewCard>
      )}
    </>
  );
};
